import { Button, Form, LayoutGrid, Spinner } from '@asktia/tia-ui'
import { useSearchParams } from 'react-router-dom'
import { useNavigation } from 'src/hooks'

import { isItemEnabled } from '@asktia/tia-fhir-questionnaire'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useQuestionnaire } from 'src/flows/questionnaires/hooks/useQuestionnaire'
import { useQuestionnaireResponse } from 'src/flows/questionnaires/hooks/useQuestionnaireResponse'
import { useState } from 'react'
import { GluedSingleChoiceQuestion } from 'src/flows/questionnaires/components/Input/SingleChoice'
import { PregnancyGroupQuestion } from 'src/flows/questionnaires/Uhr/pages/Gynecology/components/PregnancyGroupQuestion'
import { get as _get, omit as _omit } from 'lodash'
import { QuestionFormGlue } from 'src/flows/questionnaires/types'
import { GluedDateQuestion } from 'src/flows/questionnaires/components/Input/DateInput'
import { Divider } from 'src/components/Blocks'
import { GluedMultiChoiceQuestion } from '../../../components/Input/MultiChoice'
import { GluedOpenInputQuestion } from 'src/flows/questionnaires/components/Input/OpenInput'
import { getFormDone } from 'src/flows/questionnaires/getFormDone'
import { ErrorSavingBanner } from '../../../components/ErrorSavingBanner'
import { yupResolver } from '@hookform/resolvers'
import { GynecologySchema } from 'src/flows/questionnaires/Uhr/pages/Gynecology/Gynecology.schema'

const PregnancyQuestion = ({
    questionnaire,
    questionnaireResponse,
    answersMap,
    triggerSave
}: QuestionFormGlue) => {
    const pregnanciesWhenQuestionConfig = questionnaire.item?.find(
        item => item.linkId === 'group.pregnancies'
    )

    return (
        <>
            {questionnaireResponse &&
                pregnanciesWhenQuestionConfig &&
                isItemEnabled(
                    pregnanciesWhenQuestionConfig,
                    questionnaireResponse
                ) && (
                    <PregnancyGroupQuestion
                        questionnaire={questionnaire}
                        questionnaireResponse={questionnaireResponse}
                        answersMap={answersMap}
                        triggerSave={triggerSave}
                    />
                )}
        </>
    )
}

const Gynecology = () => {
    const [searchParams] = useSearchParams()
    const { navigate } = useNavigation()
    const [showSavingError, setShowSavingError] = useState(false)

    const formMethods = useForm<FieldValues>({
        defaultValues: {},
        mode: 'all',
        resolver: yupResolver(GynecologySchema)
    })

    const { questionnaire } = useQuestionnaire('gynecology')
    const { answersMap, questionnaireResponse, saveQuestionnaireResponse } =
        useQuestionnaireResponse(
            'gynecology',
            searchParams.get('appointmentProfileUuid') as string
        )
    const { formDone } = getFormDone(formMethods.getValues())

    const onComplete = async () => {
        triggerSave()
        navigate(searchParams.get('onCompleteRelativePath') as string)
    }

    const getValidFormValues = async (triggeredByFieldName?: string) => {
        // validate form against latest form values
        await formMethods.trigger(triggeredByFieldName)

        const formValues = formMethods.getValues()
        const formKeys = Object.keys(formValues)

        // TODO: not sure best way to handle this any
        // at the moment the formValues also have an any type
        const validFormValues: any = {}
        const fieldErrors = formMethods.formState.errors

        // Note - this only works for 1 nested level
        // i.e.
        // formValues = {nicotine-use: {polar: 'yes', quantity: 'asdfasdf'}}
        // errors = {nicotine-use: {quantity: 'must be a number'}}
        // validFormValues = {nicotine-use: {polar: 'yes'}
        formKeys.forEach((key: string) => {
            const fieldValue = _get(formValues, key)
            const nestedFieldErrors = _get(fieldErrors, key)
            validFormValues[key as string] =
                typeof fieldValue === 'object' && !Array.isArray(fieldValue)
                    ? _omit(fieldValue, Object.keys(nestedFieldErrors || {}))
                    : fieldValue
        })

        return validFormValues
    }

    // Save questionnaire and handle errors:
    // - clear triggering field, so user knows what's wrong
    // - show error toast
    async function triggerSave(triggeredByFieldName?: string) {
        setShowSavingError(false)

        try {
            await saveQuestionnaireResponse(
                await getValidFormValues(triggeredByFieldName)
            )
        } catch (e) {
            console.error(e)
            if (triggeredByFieldName) {
                formMethods.setValue(triggeredByFieldName, undefined)
            }
            setShowSavingError(true)
        }
    }

    if (!questionnaire.item) {
        return <Spinner />
    }

    const questionFormGlue = {
        questionnaire,
        questionnaireResponse,
        answersMap,
        triggerSave
    }

    const canProceed = formDone && Object.keys(formMethods.errors).length === 0

    return (
        <>
            {showSavingError ? <ErrorSavingBanner /> : null}
            <LayoutGrid columns="one" variant="condensed">
                <FormProvider {...formMethods}>
                    <Form
                        useFormMethods={formMethods}
                        onSubmit={() => console.log('on submit')}
                    >
                        <GluedSingleChoiceQuestion
                            linkId="period-polar"
                            {...questionFormGlue}
                        />

                        <GluedOpenInputQuestion
                            linkId="when.period"
                            variant="info"
                            placeholder="Age"
                            sx={{ mb: -3 }}
                            {...questionFormGlue}
                        />

                        <Divider variant="blockSpacer" />

                        <GluedMultiChoiceQuestion
                            linkId="contraception-methods"
                            {...questionFormGlue}
                        />

                        <Divider variant="blockSpacer" />

                        <GluedSingleChoiceQuestion
                            linkId="sexually.active-polar"
                            {...questionFormGlue}
                        />

                        <GluedMultiChoiceQuestion
                            linkId="kind.sex"
                            variant="info"
                            {...questionFormGlue}
                        />

                        <GluedMultiChoiceQuestion
                            linkId="sexual.patterns"
                            variant="info"
                            followupCardSx={{ mt: -5, px: 4, py: 5 }}
                            {...questionFormGlue}
                        />

                        <GluedDateQuestion
                            variant="info"
                            linkId="last.sti.test"
                            followupCardSx={{ mt: -5, px: 4, py: 5 }}
                            customNilnknownText="I’ve never done this, or this doesn’t apply to me."
                            {...questionFormGlue}
                        />

                        <GluedSingleChoiceQuestion
                            linkId="new.partners.after.sti.test"
                            variant="info"
                            followupCardSx={{ mt: -5, px: 4, py: 5 }}
                            {...questionFormGlue}
                        />

                        <Divider variant="blockSpacer" />

                        <GluedDateQuestion
                            linkId="last.pap.test"
                            customNilnknownText="I’ve never done this, or this doesn’t apply to me."
                            {...questionFormGlue}
                        />

                        <Divider variant="blockSpacer" />

                        <GluedSingleChoiceQuestion
                            linkId="abnormal.pap.test-polar"
                            {...questionFormGlue}
                        />

                        <GluedDateQuestion
                            linkId="date.abnormal.pap.test"
                            variant="info"
                            {...questionFormGlue}
                        />

                        <GluedMultiChoiceQuestion
                            linkId="treatment.abnormal.pap.test"
                            variant="info"
                            followupCardSx={{ mt: -5, px: 4, py: 5 }}
                            {...questionFormGlue}
                        />

                        <Divider variant="blockSpacer" />

                        <GluedSingleChoiceQuestion
                            linkId="current.pregnant-polar"
                            {...questionFormGlue}
                        />

                        <Divider variant="blockSpacer" />

                        <GluedSingleChoiceQuestion
                            linkId="been.pregnant-polar"
                            {...questionFormGlue}
                        />

                        <PregnancyQuestion {...questionFormGlue} />

                        <Divider variant="blockSpacer" />

                        <GluedSingleChoiceQuestion
                            linkId="plan.pregnancy-polar"
                            {...questionFormGlue}
                        />

                        <Button
                            sx={{ mt: 6, mb: 8, py: '20px' }}
                            onClick={onComplete}
                            disabled={!canProceed}
                            fullWidth
                        >
                            Complete Gynecology
                        </Button>
                    </Form>
                </FormProvider>
            </LayoutGrid>
        </>
    )
}

export default Gynecology
